import { useState, useEffect, useMemo, useRef } from 'react';
import Card from '../components/common/Card.jsx';
import Table from '../components/common/Table.jsx';
import MultiSelect from '../components/common/MultiSelect.jsx';
import DonutChart from '../components/charts/DonutChart.jsx';
import SimpleLineChart from '../components/charts/SimpleLineChart.jsx';
import HorizontalBarChart from '../components/charts/HorizontalBarChart.jsx';
import GroupedBarChart from '../components/charts/GroupedBarChart.jsx';

import KpiRow from '../components/optimization/KpiRow.jsx';
import TopActionsCard from '../components/optimization/TopActionsCard.jsx';
import AIModal from '../components/optimization/AIModal.jsx';
import { BadgeSkeleton } from '../components/optimization/Skeletons.jsx';
import { CostCell, RecommendedCostCell, SavingCell, RecCell, IdCell } from '../components/optimization/RecCells.jsx';
import {
  parseCost, formatSavingStr,
  buildBaseCostKpis, exportCSV,
  getScopedItem, setScopedItem, STORAGE_KEYS,
} from '../components/common/Helper';

import RecommendationApiService from '../services/RecommendationApiService';
import InventoryApiService from '../services/InventoryApiService';
import APIConstants from '../services/APIConstants';
import { useAuth } from '../state/AuthContext';

// ── Tabs ─────────────────────────────────────────────────────

const subTabs = [
  { label: 'Combined Recommendations' },
  { label: 'CloudsAnalytics Recommendations' },
  { label: 'Azure Recommendations' },
];

// type sent to /api/azure/recommendations, keyed by sub-tab index
const TAB_TYPES = ['COMBINED_CONTAINER_INSTANCE', 'CUSTOM_CONTAINER_INSTANCE', 'CONTAINER_INSTANCE'];

// ── Rec helpers ──────────────────────────────────────────────

const resolveRec = (recType) => {
  const t = (recType ?? '').toUpperCase().trim();
  let recPillClass = 'rec-ok';
  if (['DELETE', 'REMOVE', 'REMOVEUNUSEDRESOURCE'].includes(t))                            recPillClass = 'rec-id';
  else if (['RESIZE', 'RIGHTSIZE', 'DOWNSIZE', 'DOWNGRADE'].includes(t))                   recPillClass = 'rec-rs';
  else if (['MOVE', 'MOVE_TO_CONTAINER_APPS', 'MOVETOCONTAINERAPPS'].includes(t))          recPillClass = 'rec-tier';
  return { recType: t || 'OPTIMAL', recPillClass };
};

const notNA = (v) => (v != null && String(v).trim() !== '' && String(v).trim().toUpperCase() !== 'NA') ? v : null;

const toNum = (v) => { const n = Number(v); return Number.isFinite(n) ? n : null; };

// ── Row Mapper ───────────────────────────────────────────────
// All three tabs share one item shape; source-specific keys (impact,
// avgCpuUsage, containerGroupName…) are read defensively.

const toContainerRow = (item) => {
  const current     = parseCost(item.currentMonthlyPriceValue);
  const recommended = parseCost(item.recommendedMonthlyPriceValue);
  const saving      = parseCost(item.savingCost ?? Math.max(0, current - recommended));

  const rawRec = item.recommendationType ?? item.recommendationSubCategory ?? (saving > 0 ? 'RESIZE' : 'OPTIMAL');
  const { recType, recPillClass } = resolveRec(rawRec);

  // Show the backend detail exactly; NA / absent falls through, finally "—".
  const remark =
    notNA(item.recommendationDetail) ??
    notNA(item.remark) ??
    notNA(item.recommendationSubCategory) ??
    '—';

  const containerCount = Array.isArray(item.containers)
    ? item.containers.length
    : (toNum(item.containerCount) ?? null);

  return {
    id: item.id,
    containerName: item.containerName ?? item.containerGroupName ?? item.name ?? '—',
    location: item.location ?? item.resourceLocation ?? '—',
    resourceGroup: item.resourceGroup ?? '—',
    os: item.osType ?? item.os ?? '—',
    skuName: item.skuName ?? '—',
    ipAddress: item.ipAddress ?? '—',
    impact: item.impact ?? '—',
    restartPolicy: item.restartPolicy ?? '—',
    allocatedCpu: toNum(item.allocatedCpu),
    allocatedMemory: toNum(item.allocatedMemory),
    recommendedCpu: toNum(item.recommendedCpu),
    recommendedMemory: toNum(item.recommendedMemory),
    avgCpu: toNum(item.avgCpuPercent ?? item.avgCpuUsage),
    peakCpu: toNum(item.peakCpuPercent),
    avgMem: toNum(item.avgMemoryPercent ?? item.avgMemoryUsage),
    peakMem: toNum(item.peakMemoryPercent),
    containerCount,
    containers: Array.isArray(item.containers) ? item.containers : [],
    status: item.status ?? item.provisioningState ?? '—',
    provisioningState: item.provisioningState ?? '—',
    timeCreated: item.timeCreated ?? null,
    cost: `$${current.toFixed(2)}`,
    costValue: current,
    recommendedCost: `$${recommended.toFixed(2)}`,
    recommendedCostValue: recommended,
    recommendationRemark: remark,
    recType,
    recPillClass,
    saving: formatSavingStr(saving),
    savingValue: saving,
    recommendedBy: item.recommendedBy ?? '—',
    actionLabel: 'Assist with AI',
    actionClass: 'mini-btn ai-assist',
  };
};

const mapRows = (data) => (data ?? []).map(toContainerRow);

// ── KPI ──────────────────────────────────────────────────────

const buildKpiData = (totalMonthlyCost, rows) => {
  const rowCurrent = rows.reduce((s, r) => s + r.costValue, 0);
  const rowRec     = rows.reduce((s, r) => s + r.recommendedCostValue, 0);
  const rowSaving  = rows.reduce((s, r) => s + r.savingValue, 0);

  const current     = totalMonthlyCost != null ? parseCost(totalMonthlyCost.currentCost)     : rowCurrent;
  const recommended = totalMonthlyCost != null ? parseCost(totalMonthlyCost.recommendedCost) : rowRec;
  const saving      = totalMonthlyCost != null ? parseCost(totalMonthlyCost.savingCost)      : rowSaving;

  const totalVcpu = rows.reduce((s, r) => s + (r.allocatedCpu || 0), 0);
  const totalMem  = rows.reduce((s, r) => s + (r.allocatedMemory || 0), 0);
  const idleRows  = rows.filter((r) => ['DELETE', 'REMOVE', 'REMOVEUNUSEDRESOURCE'].includes(r.recType));
  const idleCost  = idleRows.reduce((s, r) => s + r.costValue, 0);

  return buildBaseCostKpis({
    countTitle: 'Container Groups',
    countValue: rows.length,
    countSubtitle: `${totalVcpu} vCPU · ${totalMem} GiB provisioned`,
    current, saving, recommended,
    kpi4Title: 'Idle Group Cost',
    kpi4Value: `$${idleCost.toFixed(2)}`,
    kpi4Subtitle: `${idleRows.length} idle group${idleRows.length !== 1 ? 's' : ''} to delete`,
  });
};

// ── Charts ────────────────────────────────────────────────────

// Container utilization trend — driven by CONTAINER_INSTANCE_TREND API
// ([{ date, avgCpuPercent, avgMemoryPercent }]).
const buildTrendLine = (arr) => {
  if (!Array.isArray(arr) || !arr.length) return null;
  return {
    labels: arr.map((d) => new Date(d.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })),
    datasets: [
      {
        label: 'Avg CPU %',
        data: arr.map((d) => parseFloat(Number(d.avgCpuPercent ?? 0).toFixed(1))),
        borderColor: '#0078d4',
        backgroundColor: 'rgba(0,120,212,0.15)',
        fill: true,
        tension: 0.3,
        borderWidth: 2,
        pointRadius: 0,
      },
      {
        label: 'Avg Memory %',
        data: arr.map((d) => parseFloat(Number(d.avgMemoryPercent ?? 0).toFixed(1))),
        borderColor: '#f57c00',
        backgroundColor: 'transparent',
        fill: false,
        tension: 0.3,
        borderWidth: 2,
        pointRadius: 0,
        borderDash: [4, 3],
      },
    ],
  };
};

const buildOsDonut = (rows) => {
  if (!rows.length) return null;
  const hasCost = rows.some((r) => r.costValue > 0);
  const buckets = {};
  rows.forEach((r) => {
    const key = (r.os && r.os !== '—') ? r.os : 'Unknown';
    buckets[key] = (buckets[key] || 0) + (hasCost ? r.costValue : 1);
  });
  const labels = Object.keys(buckets);
  if (!labels.length) return null;
  const palette = { Linux: '#0078d4', Windows: '#f57c00', Unknown: '#9aa3b7' };
  return {
    labels,
    datasets: [{
      data: labels.map((k) => parseFloat(buckets[k].toFixed(2))),
      backgroundColor: labels.map((k) => palette[k] ?? '#26c6da'),
      borderWidth: 2,
      borderColor: '#fff',
    }],
  };
};

const buildAllocVsRec = (rows) => {
  const named = rows.filter((r) => r.containerName !== '—' && r.allocatedCpu != null).slice(0, 6);
  if (!named.length) return null;
  return {
    labels: named.map((r) => (r.containerName.length > 16 ? r.containerName.slice(0, 15) + '…' : r.containerName)),
    datasets: [
      { label: 'Allocated vCPU', data: named.map((r) => r.allocatedCpu || 0), backgroundColor: '#f57c00', borderRadius: 4, barPercentage: 0.7 },
      { label: 'Recommended vCPU', data: named.map((r) => r.recommendedCpu || 0), backgroundColor: '#2ea66f', borderRadius: 4, barPercentage: 0.7 },
    ],
  };
};

const buildSavingsByAction = (rows) => {
  const buckets = { 'Delete idle': 0, 'Right-size': 0, 'Move to Container Apps': 0 };
  rows.forEach((r) => {
    const s = r.savingValue;
    if (s <= 0) return;
    if (['DELETE', 'REMOVE', 'REMOVEUNUSEDRESOURCE'].includes(r.recType)) buckets['Delete idle'] += s;
    else if (['MOVE', 'MOVE_TO_CONTAINER_APPS', 'MOVETOCONTAINERAPPS'].includes(r.recType)) buckets['Move to Container Apps'] += s;
    else buckets['Right-size'] += s;
  });
  if (!Object.values(buckets).some((v) => v > 0)) return null;
  return {
    labels: Object.keys(buckets),
    datasets: [{
      label: 'Saving ($)',
      data: Object.values(buckets).map((v) => parseFloat(v.toFixed(2))),
      backgroundColor: ['#ef4444', '#f57c00', '#0078d4'],
      borderRadius: 4,
      barPercentage: 0.6,
    }],
  };
};

const buildInsightActions = (rows) => {
  const withSaving = rows
    .filter((r) => r.savingValue > 0)
    .sort((a, b) => b.savingValue - a.savingValue);
  const totalSaving = withSaving.reduce((s, r) => s + r.savingValue, 0);
  const actions = withSaving.map((r, i) => {
    let actionLabel = 'Review →'; let actionClass = 'mini-btn';
    if (['DELETE', 'REMOVE', 'REMOVEUNUSEDRESOURCE'].includes(r.recType)) { actionLabel = 'Delete →'; actionClass = 'mini-btn ghost'; }
    else if (['RESIZE', 'RIGHTSIZE', 'DOWNSIZE', 'DOWNGRADE'].includes(r.recType)) actionLabel = 'Rightsize →';
    else if (['MOVE', 'MOVE_TO_CONTAINER_APPS', 'MOVETOCONTAINERAPPS'].includes(r.recType)) actionLabel = 'Move →';
    return {
      id: i + 1,
      title: r.containerName || r.id,
      amount: r.saving,
      desc: `${r.os} · ${r.allocatedCpu ?? '—'} vCPU / ${r.allocatedMemory ?? '—'} GiB · ${r.location}`,
      actionLabel,
      actionClass,
      row: r,
    };
  });
  return { actions, totalSaving };
};

// ── Cells ─────────────────────────────────────────────────────

const OsCell = ({ row }) => {
  if (!row.os || row.os === '—') return <span style={{ color: 'var(--muted)' }}>—</span>;
  const isWin = row.os.toLowerCase().includes('windows');
  return (
    <span style={{ background: isWin ? '#e3f2fd' : '#e8f5e9', color: isWin ? '#1565c0' : '#2e7d32', padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600 }}>
      {row.os}
    </span>
  );
};

const CpuUtilCell = ({ row }) => {
  if (row.avgCpu == null) return <span style={{ color: 'var(--muted)' }}>—</span>;
  const pct   = Math.min(100, Math.max(0, row.avgCpu));
  const color = pct < 35 ? '#2ea66f' : pct < 70 ? '#f57c00' : '#ef4444';
  const title = row.peakCpu != null ? `Peak ${row.peakCpu}%` : undefined;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 90 }} title={title}>
      <div style={{ flex: 1, height: 6, background: '#eef1f7', borderRadius: 4, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 4, transition: 'width .3s' }} />
      </div>
      <span style={{ fontSize: 11, color, fontWeight: 600, whiteSpace: 'nowrap' }}>{pct.toFixed(0)}%</span>
    </div>
  );
};

// ── Table Columns ─────────────────────────────────────────────

const containerColumns = [
  { key: 'id',            label: 'Id', render: (row) => <IdCell row={row} /> },
  { key: 'containerName', label: 'Container Name' },
  { key: 'location',      label: 'Location' },
  { key: 'resourceGroup', label: 'Resource Group' },
  { key: 'os',            label: 'OS', render: (row) => <OsCell row={row} /> },
  { key: 'skuName',       label: 'SKU' },
  {
    key: 'allocated', label: 'Allocated', className: 'num',
    render: (row) => row.allocatedCpu == null
      ? '—'
      : <span title={`${row.containerCount ?? 0} container(s)`}>{row.allocatedCpu} vCPU / {row.allocatedMemory} GiB</span>,
  },
  {
    key: 'recommended', label: 'Recommended', className: 'num',
    render: (row) => row.recommendedCpu == null
      ? <span style={{ color: 'var(--muted)' }}>—</span>
      : <span style={{ fontWeight: 600 }}>{row.recommendedCpu} vCPU / {row.recommendedMemory} GiB</span>,
  },
  { key: 'avgCpu', label: 'Avg CPU', render: (row) => <CpuUtilCell row={row} /> },
  {
    key: 'avgMem', label: 'Avg Mem', className: 'num',
    render: (row) => row.avgMem != null ? `${row.avgMem}%` : <span style={{ color: 'var(--muted)' }}>—</span>,
  },
  {
    key: 'status', label: 'Status',
    render: (row) => {
      const ok = ['running', 'succeeded'].includes((row.status ?? '').toLowerCase());
      return <span className={`status-pill ${ok ? 'st-running' : 'st-stopped'}`}>{row.status}</span>;
    },
  },
  { key: 'cost',                 label: 'Cost / Mo',         className: 'num', render: (row) => <CostCell row={row} /> },
  { key: 'recommendationRemark', label: 'Recommendation',    render: (row) => <RecCell row={row} /> },
  { key: 'saving',               label: 'Saving / Mo',       className: 'num', render: (row) => <SavingCell row={row} /> },
  { key: 'recommendedCost',      label: 'Payable Cost / Mo', className: 'num', render: (row) => <RecommendedCostCell row={row} /> },
  {
    key: 'action', label: 'Action',
    render: (row) => (
      <button className={row.actionClass} onClick={row.onAction ? () => row.onAction(row) : undefined}>
        {row.actionLabel}
      </button>
    ),
  },
];

// ── Main Page ─────────────────────────────────────────────────

const AzureContainerInstanceOptimization = () => {
  const { selectedAccount } = useAuth();

  const [activeTab, setActiveTab] = useState(0);

  const [isLoadingCombined, setIsLoadingCombined] = useState(false);
  const [isLoadingCustom,   setIsLoadingCustom]   = useState(false);
  const [isLoadingAzure,    setIsLoadingAzure]    = useState(false);

  const [combinedRows, setCombinedRows] = useState([]);
  const [customRows,   setCustomRows]   = useState([]);
  const [azureRows,    setAzureRows]    = useState([]);

  const [combinedKpi, setCombinedKpi] = useState([]);
  const [customKpi,   setCustomKpi]   = useState([]);
  const [azureKpi,    setAzureKpi]    = useState([]);

  const [trendData,      setTrendData]      = useState(null);
  const [isLoadingTrend, setIsLoadingTrend] = useState(false);
  const [osData,         setOsData]         = useState(null);
  const [allocData,      setAllocData]      = useState(null);
  const [savingsData,    setSavingsData]    = useState(null);
  const [insightActions, setInsightActions] = useState([]);
  const [insightTotal,   setInsightTotal]   = useState(0);

  const [tagList,        setTagList]        = useState([]);
  const [filterList,     setFilterList]     = useState({ tag: '', values: [] });
  const [locationFilter, setLocationFilter] = useState('');
  const [osFilter,       setOsFilter]       = useState('');
  const [statusFilter,   setStatusFilter]   = useState('');

  const [aiOpen,   setAiOpen]   = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiData,   setAiData]   = useState(null);

  const getUserId = () => {
    const raw = getScopedItem(STORAGE_KEYS.LOCAL_USER_DETAILS);
    if (!raw) return null;
    try { return JSON.parse(raw)?.userId ?? null; } catch { return null; }
  };

  const handleDetailsClick = (row) => {
    setAiData({
      groupId: row.id, containerName: row.containerName, location: row.location,
      resourceGroup: row.resourceGroup, os: row.os, sku: row.skuName,
      allocatedCpu: row.allocatedCpu, allocatedMemory: row.allocatedMemory,
      recommendedCpu: row.recommendedCpu, recommendedMemory: row.recommendedMemory,
      status: row.status, avgCpu: row.avgCpu, avgMemory: row.avgMem,
      currentMonthlyCost: row.cost, recommendation: row.recType, estimatedMonthlySaving: row.saving,
    });
    setAiPrompt(
      'You are a cloud cost advisor. Based on the container instance data provided, give a concise, natural ' +
      'insight directly to the user — mention the cost situation, savings within reach, and what they should ' +
      'consider doing. Keep it conversational and to the point. Do not mention field names or JSON.'
    );
    setAiOpen(true);
  };

  const fetchTagList = () => {
    if (!selectedAccount) return;
    setTagList([]);
    if (filterList.tag !== '' || filterList.values.length > 0) setFilterList({ tag: '', values: [] });
    InventoryApiService.getAzureFilterTagList(
      APIConstants.ENDPOINT_AZURE_FILTER_TAG,
      { type: 'CONTAINER_INSTANCE', userAccountId: selectedAccount }
    )
      .then((res) => setTagList(res?.data ?? []))
      .catch(() => {});
  };

  // Utilization trend is account-level → fetched once per account.
  const fetchTrendData = (userId) => {
    setIsLoadingTrend(true);
    setTrendData(null);
    RecommendationApiService.getAzureData({ userId, type: 'CONTAINER_INSTANCE_TREND', userAccountId: selectedAccount })
      .then((res) => setTrendData(buildTrendLine(res?.data)))
      .catch((err) => console.error('Error fetching container instance trend', err))
      .finally(() => setIsLoadingTrend(false));
  };

  const fetchedTabsRef = useRef(new Set());

  const fetchCombinedData = (userId) => {
    setIsLoadingCombined(true);
    setCombinedRows([]);
    RecommendationApiService.getAzureData({ userId, type: TAB_TYPES[0], userAccountId: selectedAccount })
      .then((res) => {
        const rows = mapRows(res?.data);
        setCombinedRows(rows);
        setCombinedKpi(buildKpiData(res?.totalMonthlyCost, rows));
      })
      .catch((err) => console.error('Error fetching combined container recommendations', err))
      .finally(() => setIsLoadingCombined(false));
  };

  const fetchCustomData = (userId) => {
    setIsLoadingCustom(true);
    setCustomRows([]);
    RecommendationApiService.getAzureData({
      userId, type: TAB_TYPES[1], userAccountId: selectedAccount,
      filterKey: filterList.tag || undefined,
      filterValues: filterList.values.length > 0 ? filterList.values : undefined,
    })
      .then((res) => {
        const rows = mapRows(res?.data);
        setCustomRows(rows);
        setCustomKpi(buildKpiData(res?.totalMonthlyCost, rows));
      })
      .catch((err) => console.error('Error fetching custom container recommendations', err))
      .finally(() => setIsLoadingCustom(false));
  };

  const fetchAzureData = (userId) => {
    setIsLoadingAzure(true);
    setAzureRows([]);
    RecommendationApiService.getAzureData({ userId, type: TAB_TYPES[2], userAccountId: selectedAccount })
      .then((res) => {
        const rows = mapRows(res?.data);
        setAzureRows(rows);
        setAzureKpi(buildKpiData(res?.totalMonthlyCost, rows));
      })
      .catch((err) => console.error('Error fetching azure container recommendations', err))
      .finally(() => setIsLoadingAzure(false));
  };

  // Lazy fetch: only hits the API for a tab the first time it is opened.
  const fetchTabData = (tabIndex, isForceRefresh = false) => {
    if (!selectedAccount) return;
    if (!isForceRefresh && fetchedTabsRef.current.has(tabIndex)) return;
    fetchedTabsRef.current.add(tabIndex);

    const userId = getUserId();
    if (!userId) return;

    if (tabIndex === 0) fetchCombinedData(userId);
    else if (tabIndex === 1) fetchCustomData(userId);
    else if (tabIndex === 2) fetchAzureData(userId);
  };

  const handleTabChange = (i) => {
    setActiveTab(i);
    fetchTabData(i);
  };

  const handleRefresh = () => {
    fetchedTabsRef.current.delete(activeTab);
    fetchTabData(activeTab, true);
    fetchTagList();
    const userId = getUserId();
    if (userId) fetchTrendData(userId);
  };

  const handleFilterChange = (field, value) => {
    if (field === 'tag') { setFilterList({ tag: value ?? '', values: [] }); return; }
    if (field === 'values') setFilterList((prev) => ({ tag: prev.tag, values: typeof value === 'string' ? value.split(',') : (value ?? []) }));
  };

  useEffect(() => {
    if (selectedAccount) {
      setFilterList({ tag: '', values: [] });
      fetchedTabsRef.current = new Set();
      setActiveTab(0);
      fetchTabData(0);
      fetchTagList();
      const userId = getUserId();
      if (userId) fetchTrendData(userId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccount]);

  // CloudsAnalytics (custom) tag filters are server-side — refetch only while its tab is active.
  useEffect(() => {
    if (activeTab !== 1 || !selectedAccount) return;
    if (filterList.tag === '' || filterList.values.length > 0) {
      const userId = getUserId();
      if (userId) {
        fetchedTabsRef.current.add(1);
        fetchCustomData(userId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterList]);

  const activeKpi       = activeTab === 0 ? combinedKpi       : activeTab === 1 ? customKpi       : azureKpi;
  const isLoadingActive = activeTab === 0 ? isLoadingCombined : activeTab === 1 ? isLoadingCustom : isLoadingAzure;
  const baseRows        = activeTab === 0 ? combinedRows      : activeTab === 1 ? customRows      : azureRows;

  const locations = useMemo(() => [...new Set(baseRows.map((r) => r.location).filter((v) => v && v !== '—'))].sort(), [baseRows]);
  const oses      = useMemo(() => [...new Set(baseRows.map((r) => r.os).filter((v) => v && v !== '—'))].sort(), [baseRows]);
  const statuses  = useMemo(() => [...new Set(baseRows.map((r) => r.status).filter((v) => v && v !== '—'))].sort(), [baseRows]);

  const filteredRows = useMemo(() => {
    let rows = baseRows;
    if (locationFilter) rows = rows.filter((r) => r.location === locationFilter);
    if (osFilter)       rows = rows.filter((r) => r.os === osFilter);
    if (statusFilter)   rows = rows.filter((r) => (r.status ?? '').toLowerCase() === statusFilter.toLowerCase());
    return rows;
  }, [baseRows, locationFilter, osFilter, statusFilter]);

  const activeRows = filteredRows.map((row) => ({ ...row, onAction: handleDetailsClick }));

  useEffect(() => {
    if (filteredRows.length > 0) {
      setOsData(buildOsDonut(filteredRows));
      setAllocData(buildAllocVsRec(filteredRows));
      setSavingsData(buildSavingsByAction(filteredRows));
      const { actions, totalSaving } = buildInsightActions(filteredRows);
      setInsightActions(actions);
      setInsightTotal(totalSaving);
    } else {
      setOsData(null); setAllocData(null); setSavingsData(null);
      setInsightActions([]); setInsightTotal(0);
    }
  }, [filteredRows]);

  // Keep AI chatbot context in sync with what's on screen
  useEffect(() => {
    setScopedItem(STORAGE_KEYS.AI_CHATBOT_REQUEST_DATA, {
      page: 'Container Instance Optimization',
      kpi: activeKpi,
      containerRecommendations: filteredRows,
    });
  }, [filteredRows, activeKpi]);

  const handleExport = () =>
    exportCSV(activeRows, containerColumns.filter((c) => c.key !== 'action'), `azure-container-instance-tab${activeTab}.csv`);

  const clearLocalFilters = () => {
    setLocationFilter(''); setOsFilter(''); setStatusFilter('');
    setFilterList({ tag: '', values: [] });
  };

  return (
    <>
      <div className="breadcrumb">
        Optimization <span style={{ color: 'var(--muted-2)' }}>›</span> <strong>Container Instance</strong>
      </div>

      <div className="page-head">
        <div>
          <h1>
            Container Instance Optimization{' '}
            {isLoadingActive ? <BadgeSkeleton /> : <span className="badge">{baseRows.length} groups</span>}
          </h1>
          <div className="meta">Azure Container Instances · delete idle groups, right-size over-provisioned, move to Container Apps</div>
        </div>
        <div className="head-actions">
          <button className="btn ghost" onClick={handleExport}>⬇ Export</button>
          <button className="btn ghost" onClick={handleRefresh}>↻ Refresh</button>
        </div>
      </div>

      <KpiRow kpiData={activeKpi} loading={isLoadingActive} />

      {/* Charts */}
      <div className="grid-3" style={{ marginTop: '4px' }}>
        <Card className="first" title="Container utilization — 30 days" subtitle="Avg vCPU vs memory usage across all groups">
          <SimpleLineChart data={trendData} loading={isLoadingTrend} stacked={false} yTickCallback={(v) => v + '%'} />
        </Card>
        <Card className="first" title="Cost by OS" subtitle="Linux vs Windows">
          <DonutChart data={osData} loading={isLoadingActive} />
        </Card>
        <TopActionsCard
          title="Top container actions"
          actions={insightActions}
          total={insightTotal}
          loading={isLoadingActive}
          entityName="groups"
          onAction={(item) => item.row && handleDetailsClick(item.row)}
        />
      </div>

      {/* Sub-tabs */}
      <div className="sub-tabs">
        {subTabs.map((t, i) => (
          <div key={i} className={`sub-tab${activeTab === i ? ' active' : ''}`} onClick={() => handleTabChange(i)}>
            {t.label}
          </div>
        ))}
      </div>

      {/* Filter bar */}
      <div className="filter-bar">
        <div className="field">
          <label>Filter Tag</label>
          <select value={filterList.tag} onChange={(e) => handleFilterChange('tag', e.target.value)}>
            <option value="">None</option>
            {tagList.map((tag) => <option key={tag?.Key} value={tag?.Key}>{tag?.Key}</option>)}
          </select>
        </div>
        <div className="field">
          <label>Filter Tag Values</label>
          <MultiSelect
            options={tagList.find((t) => t?.Key === filterList.tag)?.Value ?? []}
            value={filterList.values}
            onChange={(vals) => handleFilterChange('values', vals)}
            disabled={!filterList.tag}
          />
        </div>
        <div className="field">
          <label>Location</label>
          <select value={locationFilter} onChange={(e) => setLocationFilter(e.target.value)}>
            <option value="">All locations</option>
            {locations.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
        </div>
        <div className="field">
          <label>OS</label>
          <select value={osFilter} onChange={(e) => setOsFilter(e.target.value)}>
            <option value="">All</option>
            {oses.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>
        <div className="field">
          <label>Status</label>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All</option>
            {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div className="spacer" />
        {(filterList.tag || locationFilter || osFilter || statusFilter) && (
          <button className="btn ghost" onClick={clearLocalFilters}>✕ Clear</button>
        )}
        <button className="btn ghost" onClick={handleRefresh}>↻ Refresh</button>
      </div>

      <Table
        rows={activeRows}
        columns={containerColumns}
        loading={isLoadingActive}
        skeletonRows={8}
        pageSizes={[10, 25, 50, 100]}
        defaultPageSize={10}
        defaultSortKey="saving"
        defaultSortDir="desc"
        onExport={handleExport}
        emptyText="No Container Instance recommendations"
      />

      {/* Bottom charts */}
      <div className="grid-2" style={{ marginTop: '14px' }}>
        <Card className="first" title="Allocated vs recommended vCPU" subtitle="Over-provisioned compute per group (top 6)">
          <GroupedBarChart data={allocData} loading={isLoadingActive} ySuffix="" />
        </Card>
        <Card className="first" title="Recommended savings by action" subtitle="Where the savings come from">
          <HorizontalBarChart data={savingsData} loading={isLoadingActive} />
        </Card>
      </div>

      <AIModal
        open={aiOpen}
        onClose={() => { setAiOpen(false); setAiPrompt(''); setAiData(null); }}
        prompt={aiPrompt}
        data={aiData}
      />
    </>
  );
};

export default AzureContainerInstanceOptimization;
